'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const stats = [
  {
    value: '[XX]+',
    label: 'Properties Managed',
    icon: 'BuildingOffice2Icon',
    description: 'Residential and commercial properties under active management',
  },
  {
    value: '[XX]+',
    label: 'Years in Business',
    icon: 'CalendarDaysIcon',
    description: 'Serving property owners and tenants across Kathmandu',
  },
  {
    value: '[XX]',
    label: 'Avg. Days to Rent',
    icon: 'ClockIcon',
    description: 'Average time to place a qualified tenant in a managed property',
  },
  {
    value: '[XX]+',
    label: 'Happy Owners & Tenants',
    icon: 'FaceSmileIcon',
    description: 'Property owners and tenants who trust Empire Venture Nepal',
  },
];

function StatCard({ stat, index }: { stat: typeof stats[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), index * 150);
          observer.unobserve(el);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={cardRef}
      className={`flex flex-col items-center text-center px-6 py-8 transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center"
        style={{ background: 'rgba(200,150,90,0.15)' }}>
        <Icon
          name={stat.icon as Parameters<typeof Icon>[0]['name']}
          size={22}
          className="text-accent"
        />
      </div>
      <div className="text-4xl sm:text-5xl font-bold text-white mb-1 font-mono">
        {stat.value}
      </div>
      <div className="text-sm font-semibold text-white/90 mb-2 uppercase tracking-wider">
        {stat.label}
      </div>
      <p className="text-sm text-white/50 max-w-[180px] leading-relaxed">
        {stat.description}
      </p>
    </div>
  );
}

export default function StatsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  return (
    <section ref={sectionRef} className="py-16 sm:py-20 relative overflow-hidden bg-primary">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-96 h-96 opacity-10"
          style={{ background: 'radial-gradient(ellipse, rgba(200,150,90,0.5) 0%, transparent 70%)', filter: 'blur(80px)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-64 h-64 opacity-8"
          style={{ background: 'radial-gradient(ellipse, rgba(45,106,79,0.8) 0%, transparent 70%)', filter: 'blur(60px)', opacity: 0.08 }}
        />
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2.5 mb-3">
            <div className="w-8 h-px bg-accent/60" />
            <span className="text-xs font-mono tracking-widest uppercase text-white/50">
              Our Track Record
            </span>
            <div className="w-8 h-px bg-accent/60" />
          </div>
          <h2 className="text-display font-bold text-white mb-2">
            Trusted by Property Owners Across{' '}
            <span className="text-gradient-accent">Kathmandu</span>
          </h2>
          <p className="text-white/60 text-base max-w-lg mx-auto">
            These figures reflect our commitment to reliable, professional property management.
          </p>
          <p className="text-white/35 text-xs font-mono mt-1">
            * Values are editable placeholders — update with verified figures
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-white/10 border border-white/10 rounded-2xl overflow-hidden">
          {stats.map((stat, index) => (
            <StatCard key={stat.label} stat={stat} index={index} />
          ))}
        </div>

        {/* CTA below stats */}
        <div className="mt-10 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-accent text-foreground font-semibold rounded-lg hover:bg-accent/90 transition-all hover:shadow-lg hover:-translate-y-0.5 text-sm"
          >
            <Icon name="PhoneIcon" size={16} />
            Talk to Our Team Today
          </a>
        </div>
      </div>
    </section>
  );
}